import {getLibraryQueue,subscribeLibrarySync} from '../utils/librarySync';
import {fetchWithAuth} from '../services/api';
import { useUser } from './UserContext';
import { telegramHaptic } from '../utils/telegramExtras';
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { setRating as apiSetRating, getAverageItemRating, getUserItemRating, getRatingDistribution as getRatingDistributionApi, addComment as apiAddComment, getComments as apiGetComments, deleteComment as apiDeleteComment, likeComment as apiLikeComment } from '../services/api';

const RatingContext = createContext();

// eslint-disable-next-line react-refresh/only-export-components
export const useRating = () => {
  const context = useContext(RatingContext);
  if (!context) {
    throw new Error('useRating must be used within a RatingProvider');
  }
  return context;
};

export const RatingProvider = ({ children }) => {
  const { user } = useUser();
  return <AccountRatingProvider key={user?.id ?? 'guest'} userId={user?.id} >{children}</AccountRatingProvider>;
};

const AccountRatingProvider = ({ children, userId }) => {
  const userCacheKey = userId == null ? null : `polka_user_ratings_v2_${userId}`;
  // Old shared caches have no reliable owner or scale; do not migrate them.
  const [averageRatings, setAverageRatings] = useState({});
  const [userRatings, setUserRatings] = useState(() => {
    try { return userCacheKey ? JSON.parse(localStorage.getItem(userCacheKey) || '{}') : {}; }
    catch { return {}; }
  });

  useEffect(() => {
    const invalidate = () => { setUserRatings({}); setAverageRatings({}); };
    window.addEventListener('polka:library-imported', invalidate);
    return () => window.removeEventListener('polka:library-imported', invalidate);
  }, []);

  useEffect(()=>subscribeLibrarySync(()=>{
    const q=getLibraryQueue(fetchWithAuth);if(!q || q.getState().userId!==userId)return;
    setUserRatings(previous=>Object.fromEntries(Object.entries(previous).map(([key,value])=>{
      const separator=key.indexOf('_'),category=key.slice(0,separator),id=key.slice(separator+1),row=q.lookup('rating',category,id),library=q.lookup('shelf',category,id) || q.lookup('wishlist',category,id);
      return [key,row || library ? {...value,userRating:row?.rating ?? library?.user_rating ?? null}:value];
    })));
    if(!q.getState().pending)setAverageRatings({});
  }),[userId]);

  // Для комментариев используем сервер как основной источник, localStorage как fallback-кэш
  const [comments, setComments] = useState(() => {
    try {
      const saved = localStorage.getItem('polka_comments');
      return saved ? JSON.parse(saved) : {};
    } catch {
      console.warn('Некорректные polka_comments в localStorage, сбрасываю');
      return {};
    }
  });

  // Кэш pending-запросов для комментариев по ключу `${category}_${itemId}`
  const pendingCommentFetches = useRef({});

  // Кэш pending-запросов average, чтобы не дублировать
  const pendingFetches = useRef({});

  // Сохраняем средние рейтинги в localStorage
  useEffect(() => {
    localStorage.setItem('polka_average_ratings', JSON.stringify(averageRatings));
  }, [averageRatings]);

  // Сохраняем оценки пользователя в localStorage
  useEffect(() => {
    if (userCacheKey) localStorage.setItem(userCacheKey, JSON.stringify(userRatings));
  }, [userRatings, userCacheKey]);

  // Сохраняем комментарии в localStorage
  useEffect(() => {
    localStorage.setItem('polka_comments', JSON.stringify(comments));
  }, [comments]);

  // Загрузить средний рейтинг с бэкенда для конкретного элемента
  const fetchAverageRating = useCallback(async (itemId, category) => {
    const key = `${category}_${itemId}`;

    // Если уже грузится — не дублируем
    if (pendingFetches.current[key]) {
      return pendingFetches.current[key];
    }

    const promise = (async () => {
      try {
        const requestedAt = Date.now();
        const [data, mine] = await Promise.all([
          getAverageItemRating(itemId, category),
          userId == null ? Promise.resolve(null) : getUserItemRating(itemId, category),
        ]);
        if (mine) setUserRatings(prev => {
          if (new Date(prev[key]?.updatedAt || 0).getTime() > requestedAt) return prev;
          return { ...prev, [key]: { userRating: mine.rating, updatedAt: mine.updatedAt } };
        });
        if (data && data.averageRating !== undefined) {
          setAverageRatings(prev => ({
            ...prev,
            [key]: {
              averageRating: data.averageRating,
              ratingCount: data.ratingCount || 0,
              loadedAt: Date.now()
            }
          }));
        }
      } catch (error) {
        console.warn(`Не удалось загрузить средний рейтинг для ${key}:`, error);
      } finally {
        delete pendingFetches.current[key];
      }
    })();

    pendingFetches.current[key] = promise;
    return promise;
  }, [userId]);

  // Загрузить комментарии с сервера для элемента
  const fetchCommentsFromServer = useCallback(async (itemId, category) => {
    const key = `${category}_${itemId}`;

    if (pendingCommentFetches.current[key]) {
      return pendingCommentFetches.current[key];
    }

    const promise = (async () => {
      try {
        const serverComments = await apiGetComments(category, itemId);
        if (Array.isArray(serverComments)) {
          // Преобразуем поля с сервера (snake_case → camelCase для консистентности)
          const flattenReplies = rows => rows.flatMap(row => [row, ...flattenReplies(row.replies || [])]);
          const normalized = serverComments.map(c => ({
            id: c.id,
            userId: c.user_id,
            userName: c.user_name,
            userAvatar: c.user_avatar,
            text: c.text,
            parentId: c.parent_id,
            createdAt: c.created_at,
            likes: c.likes || [],
            replies: flattenReplies(c.replies || []).map(r => ({
              id: r.id,
              userId: r.user_id,
              userName: r.user_name,
              userAvatar: r.user_avatar,
              text: r.text,
              createdAt: r.created_at
            }))
          }));

          setComments(prev => ({
            ...prev,
            [key]: normalized
          }));
          return normalized;
        }
      } catch (error) {
        console.warn(`Не удалось загрузить комментарии для ${key}:`, error);
        // Если сервер недоступен, используем локальный кэш
      } finally {
        delete pendingCommentFetches.current[key];
      }
    })();

    pendingCommentFetches.current[key] = promise;
    return promise;
  }, []);

  // Получить оценку пользователя
  const getUserRating = useCallback((itemId, category) => {
    const key = `${category}_${String(itemId)}`;
    return userRatings[key]?.userRating ?? null;
  }, [userRatings]);

  // Получить средний рейтинг для элемента (сначала локальный кэш, если свежий)
  const getAverageRating = useCallback((itemId, category) => {
    const key = `${category}_${itemId}`;
    const cached = averageRatings[key];

    // Если есть кэш и он не старше 5 минут — возвращаем его
    if (cached && cached.loadedAt) {
      return cached.averageRating == null ? null : parseFloat(cached.averageRating);
    }

    // Если нет кэша — инициируем загрузку, но возвращаем null пока
    fetchAverageRating(itemId, category);

    return cached?.averageRating !== null && cached?.averageRating !== undefined
      ? parseFloat(cached.averageRating)
      : null;
  }, [averageRatings, fetchAverageRating]);

  // Получить количество оценок
  const getRatingCount = useCallback((itemId, category) => {
    const key = `${category}_${itemId}`;
    return averageRatings[key]?.ratingCount || 0;
  }, [averageRatings]);

  // Кэш распределений по звёздам — загружаем с бэкенда
  const [ratingDistributions, setRatingDistributions] = useState({});
  const pendingDistributionFetches = useRef({});

  // Загрузить распределение с бэкенда
  const fetchRatingDistribution = useCallback(async (itemId, category) => {
    const key = `${category}_${itemId}`;
    if (pendingDistributionFetches.current[key]) {
      return pendingDistributionFetches.current[key];
    }

    const promise = (async () => {
      try {
        const data = await getRatingDistributionApi(itemId, category);
        if (data && data.distribution) {
          setRatingDistributions(prev => ({
            ...prev,
            [key]: data.distribution
          }));
        }
      } catch (error) {
        console.warn(`Не удалось загрузить распределение для ${key}:`, error);
      } finally {
        delete pendingDistributionFetches.current[key];
      }
    })();

    pendingDistributionFetches.current[key] = promise;
    return promise;
  }, []);

  // Keep saves for each item ordered and only publish confirmed ratings.
  const pendingRatingSaves = useRef({});
  const setUserRating = useCallback((itemId, category, rating) => {
    const key = `${category}_${itemId}`;
    if (pendingRatingSaves.current[key] && Object.is(pendingRatingSaves.current[key].rating, rating)) return pendingRatingSaves.current[key];
    const sessionToken = localStorage.getItem('polka_token');
    const previous = pendingRatingSaves.current[key] || Promise.resolve();
    const save = previous.catch(() => {}).then(async () => {
      if (localStorage.getItem('polka_token') !== sessionToken) throw new Error('Аккаунт изменился. Повторите оценку.');
      await apiSetRating(itemId, category, rating);
      telegramHaptic();
      setUserRatings(prev => ({
        ...prev,
        [key]: { userRating: rating, updatedAt: new Date().toISOString() }
      }));
      await Promise.allSettled([
        fetchAverageRating(itemId, category),
        fetchRatingDistribution(itemId, category)
      ]);
    });
    save.rating = rating;
    pendingRatingSaves.current[key] = save;
    const cleanup = () => {
      if (pendingRatingSaves.current[key] === save) delete pendingRatingSaves.current[key];
    };
    save.then(cleanup, cleanup);
    return save;
  }, [fetchAverageRating, fetchRatingDistribution]);

  // Получить распределение оценок по 1-10 звездам
  const getRatingDistribution = useCallback((itemId, category) => {
    const key = `${category}_${itemId}`;
    const distribution = ratingDistributions[key];

    if (distribution && Array.isArray(distribution) && distribution.length === 11) {
      return distribution;
    }

    return Array(11).fill(0);
  }, [ratingDistributions]);

  // Publish only comments confirmed by the server; failures reach the caller.
  const addComment = useCallback(async (itemId, category, text, user) => {
    if (!text.trim() || text.length > 150) throw new Error('Комментарий должен содержать от 1 до 150 символов');
    const result = await apiAddComment(category, itemId, text.trim());
    if (!result?.id) throw new Error('Сервер не подтвердил сохранение комментария');
    const comment = {
      id: result.id,
      userId: result.user_id ?? user.id,
      userName: result.user_name || user.name,
      userAvatar: result.user_avatar || user.avatar,
      text: result.text || text.trim(),
      parentId: result.parent_id ?? null,
      createdAt: result.created_at || new Date().toISOString(),
      likes: result.likes || [], replies: result.replies || []
    };
    const key = `${category}_${itemId}`;
    setComments(prev => ({ ...prev, [key]: [...(prev[key] || []).filter(c => c.id !== comment.id), comment] }));
    return comment;
  }, []);

  // Получить комментарии для элемента (сначала из кэша, инициируем загрузку с сервера)
  const getComments = useCallback((itemId, category, friendsIds = []) => {
    const key = `${category}_${itemId}`;


    // Берём из локального кэша
    const itemComments = comments[key] || [];

    // Сортируем: сначала комментарии друзей, потом остальные по дате
    return [...itemComments].sort((a, b) => {
      const aIsFriend = friendsIds.includes(a.userId);
      const bIsFriend = friendsIds.includes(b.userId);

      if (aIsFriend && !bIsFriend) return -1;
      if (!aIsFriend && bIsFriend) return 1;

      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  }, [comments]);

  // Лайкнуть/убрать лайк комментария
  const likeComment = useCallback(async (itemId, category, commentId, userId) => {
    const key = `${category}_${itemId}`;

    // Оптимистичное обновление
    setComments(prev => {
      const itemComments = prev[key] || [];
      return {
        ...prev,
        [key]: itemComments.map(comment => {
          if (comment.id === commentId) {
            const alreadyLiked = comment.likes.includes(userId);
            return {
              ...comment,
              likes: alreadyLiked
                ? comment.likes.filter(id => id !== userId)
                : [...comment.likes, userId]
            };
          }
          return comment;
        })
      };
    });

    // Не для временных комментариев (с префиксом temp_)
    if (typeof commentId === 'number') {
      try {
        await apiLikeComment(commentId);
      } catch (error) {
        console.warn('Не удалось обновить лайк на сервере:', error);
      }
    }
  }, []);

  // Добавить ответ на комментарий (отправляет на сервер)
  const addReply = useCallback(async (itemId, category, commentId, text, user) => {
    if (text.length > 150) {
      throw new Error('Ответ не может превышать 150 символов');
    }

    const key = `${category}_${itemId}`;

    // Создаём временный ответ
    const tempId = `temp_reply_${Date.now()}_${Math.random()}`;
    const tempReply = {
      id: tempId,
      userId: user.id,
      userName: user.name,
      userAvatar: user.avatar,
      text: text.trim(),
      createdAt: new Date().toISOString()
    };

    // Оптимистично добавляем локально
    setComments(prev => {
      const itemComments = prev[key] || [];
      return {
        ...prev,
        [key]: itemComments.map(comment => {
          if (comment.id === commentId) {
            return {
              ...comment,
              replies: [...comment.replies, tempReply]
            };
          }
          return comment;
        })
      };
    });

    // Отправляем на сервер как комментарий с parent_id
    try {
      const result = await apiAddComment(category, itemId, text.trim(), commentId);
      if (!result?.id) throw new Error('Сервер не подтвердил сохранение ответа');

      // Заменяем временный ответ на полученный с сервера
      if (result && result.id) {
        setComments(prev => {
          const itemComments = prev[key] || [];
          return {
            ...prev,
            [key]: itemComments.map(comment => {
              if (comment.id === commentId) {
                return {
                  ...comment,
                  replies: comment.replies.map(r =>
                    r.id === tempId
                      ? {
                          id: result.id,
                          userId: result.user_id || user.id,
                          userName: result.user_name || user.name,
                          userAvatar: result.user_avatar || user.avatar,
                          text: result.text || text.trim(),
                          createdAt: result.created_at || new Date().toISOString()
                        }
                      : r
                  )
                };
              }
              return comment;
            })
          };
        });
      }
    } catch (error) {
      setComments(prev => ({ ...prev, [key]: (prev[key] || []).map(comment =>
        comment.id === commentId ? { ...comment, replies: comment.replies.filter(reply => reply.id !== tempId) } : comment
      ) }));
      throw error;
    }

    return tempReply;
  }, []);

  // Удалить комментарий
  const deleteComment = useCallback(async (itemId, category, commentId) => {
    const key = `${category}_${itemId}`;

    // Оптимистично удаляем локально
    setComments(prev => ({
      ...prev,
      [key]: (prev[key] || []).filter(comment => comment.id !== commentId)
    }));

    // Отправляем на сервер (только для реальных ID, не временных)
    if (typeof commentId === 'number') {
      try {
        await apiDeleteComment(commentId);
      } catch (error) {
        console.warn('Не удалось удалить комментарий на сервере:', error);
        // Если не удалось — перезагрузим комментарии с сервера
        fetchCommentsFromServer(itemId, category);
      }
    }
  }, [fetchCommentsFromServer]);

  // Получить статистику
  const getStats = useCallback((itemId, category) => {
    const key = `${category}_${itemId}`;
    const itemComments = comments[key] || [];
    const cached = averageRatings[key];

    return {
      averageRating: cached?.averageRating !== undefined && cached?.averageRating !== null
        ? parseFloat(cached.averageRating)
        : null,
      ratingCount: cached?.ratingCount || 0,
      commentCount: itemComments.length,
      totalLikes: itemComments.reduce((acc, c) => acc + c.likes.length, 0)
    };
  }, [averageRatings, comments]);

  return (
    <RatingContext.Provider value={{
      averageRatings,
      userRatings,
      comments,
      setUserRating,
      getUserRating,
      getAverageRating,
      getRatingCount,
      getRatingDistribution,
      fetchAverageRating,
      fetchRatingDistribution,
      fetchCommentsFromServer,
      addComment,
      getComments,
      likeComment,
      addReply,
      deleteComment,
      getStats
    }}>
      {children}
    </RatingContext.Provider>
  );
};

export default RatingContext;
